/* Pawuni PDP live preview overlay (iter4 Phase 3c — pawuni-new-design-iteration-4-rebuild)
   Mirrors the first non-empty TeeInBlue text input value as a quiet "Live preview"
   ribbon on the main gallery image. Pure visual chrome — does NOT replace
   TeeInBlue's own preview modal, does NOT submit cart, does NOT touch ATC.

   Self-gate: exits unless `<body class="teeinblue-enabled">` is present.
   Theme owns the overlay shell + JS read; TeeInBlue still owns its own preview
   button + modal. Per `pages/_app-teeinblue.md` hard rule: theme cannot
   override app form fields, but reading their `input` events is allowed
   (the same pattern as variant change listeners).

   Known limitation: TeeInBlue's runtime rebuilds the gallery DOM on input
   change to render its own image-baked preview. The MutationObserver below
   re-injects the overlay shell after each rebuild so the badge/text stays
   present, but expect a brief flicker during personalization input.

   Author: pawuni-dev iter4 (2026-05-08).
*/
(() => {
  'use strict';

  let lastValue = '';

  const findTextInputs = () => Array.from(document.querySelectorAll(
    '.product__details--product-page input.tee__input[type="text"], ' +
    '.product__details--product-page input.tee__input:not([type]), ' +
    '.product__details--product-page input.tee-field__input[type="text"], ' +
    '.product__details--product-page textarea.tee__input'
  ));

  const computeValue = () => {
    const inputs = findTextInputs();
    for (const i of inputs) {
      const v = (i.value || '').trim();
      if (v) return v;
    }
    return '';
  };

  const buildOverlay = () => {
    const ov = document.createElement('div');
    ov.className = 'pawuni-live-preview';
    ov.setAttribute('aria-hidden', 'true');
    ov.innerHTML = [
      '<span class="pawuni-live-preview__badge">Live preview</span>',
      '<span class="pawuni-live-preview__text" data-pawuni-live-text></span>',
    ].join('');
    return ov;
  };

  const ensureOverlay = () => {
    const galleryWrap = document.querySelector(
      'body.pawuni-theme.product .shopify-section--product-template .gallery-wrap'
    );
    if (!galleryWrap) return null;
    let overlay = galleryWrap.querySelector(':scope > .pawuni-live-preview');
    if (!overlay) {
      overlay = buildOverlay();
      galleryWrap.appendChild(overlay);
    }
    return overlay;
  };

  const refresh = () => {
    const overlay = ensureOverlay();
    if (!overlay) return;
    const textEl = overlay.querySelector('[data-pawuni-live-text]');
    const value = computeValue();
    const active = Boolean(value);
    const activeMismatch = overlay.classList.contains('is-active') !== active;

    if (value === lastValue && textEl?.textContent === value && !activeMismatch) return;

    lastValue = value;
    if (value) {
      textEl.textContent = value;
      overlay.classList.add('is-active');
    } else {
      textEl.textContent = '';
      overlay.classList.remove('is-active');
    }
  };

  const wireInputs = () => {
    const inputs = findTextInputs();
    inputs.forEach((i) => {
      if (i.dataset.pawuniLiveBound === '1') return;
      i.dataset.pawuniLiveBound = '1';
      i.addEventListener('input', refresh, { passive: true });
      i.addEventListener('change', refresh, { passive: true });
    });
  };

  let booted = false;

  const isTeeinblueProductReady = () =>
    document.body &&
    document.body.classList.contains('teeinblue-enabled') &&
    document.querySelector('body.pawuni-theme.product');

  const READY = () => {
    if (booted || !isTeeinblueProductReady()) return;
    booted = true;

    ensureOverlay();
    wireInputs();
    refresh();

    const productImages = document.querySelector('.shopify-section--product-template .product__images');
    const formHost = document.getElementById('teeFormInputs') || document.querySelector('#tee-artwork-form') || document.querySelector('.product__details--product-page');
    if ('MutationObserver' in window) {
      const replant = () => { ensureOverlay(); wireInputs(); refresh(); };
      if (productImages) {
        new MutationObserver(replant).observe(productImages, { childList: true, subtree: true });
      }
      if (formHost) {
        new MutationObserver(replant).observe(formHost, { childList: true, subtree: true });
      }
    }
  };

  const waitForReady = () => {
    READY();
    if (booted || !document.body) return;

    if ('MutationObserver' in window) {
      const readyObserver = new MutationObserver(() => {
        READY();
        if (booted) readyObserver.disconnect();
      });

      readyObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        subtree: true,
      });
    }

    [250, 750, 1500, 3000, 5000].forEach((delay) => {
      window.setTimeout(READY, delay);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForReady, { once: true });
  } else {
    waitForReady();
  }
})();
